/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useCallback } from "react";
import JSZip from "jszip";
import { Button, classnames, Icon, PlainButton } from "@culturehq/components";
import client, { makePatch } from "@culturehq/client";

import { useProfile } from "app/context";
import { isAdmin, isMyOwnStory } from "app/permissions";
import { errorService, getMediaFromStory, t, Trans } from "app/utils";
import imagesSizes from "constants/imagesSizes";

import QuickOptions from "components/display/QuickOptions";
import PostStory from "components/stories/post-stories-v2/PostStory";
import StoryShare from "components/stories/StoryShare";

import VideoStudioContext from "components/stories/video-studio/contexts/VideoStudioContext";
import { NavLink, useLocation } from "react-router-dom";
import Flag from "../Flag";
import AccordianCaptions from "../AccordianCaptions";
import ChqModalConfirmDelete from "../ChqModalConfirmDelete";
import AccordionStateOptions from "../AccordionStateOptions";

const LightboxStoryQuickOptions = ({
  isLastMediaVersionProcessing,
  story,
  thirdPartyMediaIsFetching,
  thirdPartyMedias,
  onDelete,
  onPauseVideo,
  onUpdate,
  onAnotherModalOpen,
  onThirdPartyMediasReload,
  onUpdateDraftVersion,
  handleApproveClicked
}) => {
  const { profile, fetchedProfile } = useProfile();
  const {
    media: { subtitles }
  } = story;
  const [accordionOptions, setAccordionOptions] = useState([]);
  const [accordionOptionsApprove, setAccordionOptionsApprove] = useState([]);
  const [accordionOptionsInternal, setAccordionOptionsInternal] = useState([]);
  const [accordionOptionsArchived, setAccordionOptionsArchived] = useState([]);
  const itemWithOriginalTrue = thirdPartyMedias.find(item => item.original === true);
  const baseUrl = window.location.origin;
  const location = useLocation();

  const isApprovedState = ["landing_page", "approved", "social", "approved_for_everything"].includes(story.externalState);
  const isApprovedPath = window.location.pathname.startsWith("/admin/story-reviews/approved");

  const isInAdminListPage = location.pathname === "/admin/story-reviews/pending"
    || location.pathname === "/admin/story-reviews/internal"
    || location.pathname === "/admin/story-reviews/approved";

  const isInternalState = story.externalState === "internal";
  const isInternalPath = window.location.pathname.startsWith("/admin/story-reviews/internal");

  const isPendingState = story.externalState === "pending";
  const isPendingPath = window.location.pathname.startsWith("/admin/story-reviews/pending");

  const isArchivedState = story.externalState === "archived";
  const isArchivedPath = window.location.pathname.startsWith("/admin/story-reviews/archived");

  const onMoveToArchivedClicked = useCallback(async story => {
    try {
      const { story: updatedStory } = await makePatch(`/stories/move/${story.id}`, { externalState: "archived" });
      onDelete(updatedStory.id);
    } catch (error) {
      errorService.error(error);
    }
  }, [onDelete]);

  const onMoveToPendingClicked = useCallback(async story => {
    try {
      const { story: updatedStory } = await makePatch(`/stories/move/${story.id}`, { externalState: "pending" });
      onDelete(updatedStory.id);
    } catch (error) {
      errorService.error(error);
    }
  }, [onDelete]);

  const onMoveToInternalOnlyClicked = useCallback(async story => {
    try {
      const { story: updatedStory } = await makePatch(`/stories/move/${story.id}`, { externalState: "internal" });
      onDelete(updatedStory.id);
    } catch (error) {
      errorService.error(error);
    }
  }, [onDelete]);

  useEffect(() => {
    const downloadZIP = async () => {
      const zip = new JSZip();
      const videoUrl = getMediaFromStory(imagesSizes.SHAREABLE, story);
      const videoFilename = "video.mp4";
      const videoContent = await fetch(videoUrl).then(res => res.blob());
      zip.file(videoFilename, videoContent);
      await Promise.all(
        subtitles.map(async subtitle => {
          const subtitleFilename = `${subtitle.language}.vtt`;
          const subtitleSRTFilename = `${subtitle.language}.srt`;
          const subtitleContent = await fetch(subtitle.url).then(res => res.blob()
          );
          const subtitleVTTContent = await fetch(subtitle.url).then(res => res.text()
          );
          const subtitleSRTContent = convertVTTtoSRT(subtitleVTTContent);
          zip.file(subtitleFilename, subtitleContent);
          zip.file(subtitleSRTFilename, subtitleSRTContent);
        })
      );
      zip.generateAsync({ type: "blob" }).then(content => {
        const timestamp = Date.now();
        const url = window.URL.createObjectURL(content);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${timestamp}_video_and_captions.zip`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      });
    };

    const convertVTTtoSRT = vttContent => {
      const subtitleBlocks = vttContent.trim().split(/\n\s*\n/);
      const formatTime = time => time.replace(".", ",");
      let subtitleCount = 1;
      const srtContent = subtitleBlocks
        .map(subtitleBlock => {
          const lines = subtitleBlock.split("\n");
          if (lines.length >= 3) {
            const times = lines[1].split(" --> ");
            if (times.length === 2) {
              const startTime = formatTime(times[0]);
              const endTime = formatTime(times[1]);
              const text = lines.slice(2).join("\n");
              const srtSubtitle = `${subtitleCount}\n${startTime} --> ${endTime}\n${text}\n`;
              subtitleCount += 1;
              return srtSubtitle;
            }
          }
          return null;
        })
        .filter(subtitle => subtitle !== null)
        .join("\n");

      return srtContent;
    };

    const downloadImagesZip = async () => {
      const zip = new JSZip();
      const imagesUrls = story.visuals.map(str => str?.fullUrl || str?.url);
      const imagesContent = imagesUrls.map(imgUrl => fetch(imgUrl).then(res => res.blob())
      );
      imagesUrls.forEach((_, i) => {
        zip.file(`${i + 1}.jpg`, imagesContent[i]);
      });
      zip.generateAsync({ type: "blob" }).then(content => {
        const timestamp = Date.now();
        const url = window.URL.createObjectURL(content);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${timestamp}_photos.zip`;
        document.body.appendChild(link);
        link.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      });
    };

    const downloadMessage = () => {
      switch (story.media.mediaType) {
        case "image":
          return t(
            `Download ${story.visuals.length > 1 ? "photos" : "the photo"}`
          );
        default:
          return t("Download video");
      }
    };

    const mediaOption = () => {
      if (story.visuals.length > 1) {
        return {
          className: "accordion-option icon-a",
          content: (
            <PlainButton
              aria-label={t("Download")}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              <Icon icon="download" />
              <Trans>{downloadMessage()}</Trans>
            </PlainButton>
          ),
          onClick: () => {
            downloadImagesZip();
          }
        };
      }

      return {
        content: (
          <>
            <div className="accordion-option icon-a">
              <a
                aria-label={t("Download")}
                href="#"
                onClick={async e => {
                  e.preventDefault();
                  const fileUrl = getMediaFromStory(imagesSizes.SHAREABLE, story);
                  try {
                    const response = await fetch(fileUrl);
                    const blob = await response.blob();
                    const reader = new FileReader();

                    reader.onloadend = () => {
                      const timestamp = Date.now();
                      const link = document.createElement("a");
                      link.href = reader.result;
                      link.download = story.media.mediaType === "image" ? `${timestamp}_photo.jpg` : `${timestamp}_video.mp4`;
                      document.body.appendChild(link);
                      link.click();

                      setTimeout(() => {
                        document.body.removeChild(link);
                      }, 200);
                    };
                    reader.readAsDataURL(blob);
                  } catch (error) {
                    errorService.error(error);
                  }
                }}
              >
                {thirdPartyMedias.length === 0 ? (
                  <Trans>
                    Download the {story.media.mediaType === "image" ? "photo" : "video"}
                  </Trans>
                ) : (
                  <Trans>
                    Download current {story.media.mediaType === "image" ? "photo" : "video"}
                  </Trans>
                )}
              </a>
            </div>
            {thirdPartyMedias.length > 0 && itemWithOriginalTrue && (
              <div className="accordion-option icon-a">
                <a
                  aria-label={t("Download")}
                  href="#"
                  onClick={async e => {
                    e.preventDefault();
                    if (itemWithOriginalTrue && itemWithOriginalTrue.additionalData
                    && itemWithOriginalTrue.additionalData.elements) {
                      const sourceToDownload = itemWithOriginalTrue.additionalData.elements.find(
                        element => element.source)?.source;
                      if (sourceToDownload) {
                        try {
                          const timestamp = Date.now();
                          const response = await fetch(sourceToDownload);
                          const blob = await response.blob();
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const link = document.createElement("a");
                            link.href = reader.result;
                            link.download = story.media.mediaType === "image" ? `${timestamp}_photo.jpg` : `${timestamp}_video.mp4`;
                            document.body.appendChild(link);
                            link.click();
                            setTimeout(() => {
                              document.body.removeChild(link);
                            }, 200);
                          };
                          reader.readAsDataURL(blob);
                        } catch (error) {
                          errorService.error(error);
                        }
                      }
                    }
                  }}
                >
                  <Trans>
                    Download original {story.media.mediaType === "image" ? "photo" : "video"}
                  </Trans>
                </a>
              </div>
            )}
          </>
        )
      };
    };

    if (story.media.mediaType === "video" && subtitles.length > 0) {
      setAccordionOptions([
        mediaOption(),
        {
          className: "accordion-option icon-b",
          content: (
            <>
              {subtitles.length > 0 && (
                <PlainButton
                  className="download-caption-btn"
                  aria-label={t("Download")}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  <Trans>Download the video and captions file(s)</Trans>
                </PlainButton>
              )}
            </>
          ),
          onClick: () => {
            downloadZIP();
          }
        }
      ]);
    } else {
      setAccordionOptions([mediaOption()]);
    }

    setAccordionOptionsApprove([
      {
        content: (
          <>
            <div className="accordion-option icon-a">
              <a
                aria-label={t("onMoveToPendingClicked")}
                href=""
                onClick={async e => {
                  e.preventDefault();
                  await onMoveToPendingClicked(story);
                }}
              >
                <Trans>
                  Pending Review
                </Trans>
              </a>
            </div>
            <div className="accordion-option icon-a">
              <a
                aria-label={t("onMoveToInternalOnlyClicked")}
                href=""
                onClick={async e => {
                  e.preventDefault();
                  await onMoveToInternalOnlyClicked(story);
                }}
              >
                <Trans>
                  Internal Only
                </Trans>
              </a>
            </div>
            <div className="accordion-option icon-a">
              <a
                aria-label={t("onMoveToArchivedClicked")}
                href=""
                onClick={async e => {
                  e.preventDefault();
                  await onMoveToArchivedClicked(story);
                }}
              >
                <Trans>
                  Archived
                </Trans>
              </a>
            </div>
          </>
        )
      }
    ]);

    setAccordionOptionsInternal([
      {
        content: (
          <>
            <div className="accordion-option icon-a">
              <a
                aria-label={t("onMoveToPendingClicked")}
                href=""
                onClick={async e => {
                  e.preventDefault();
                  await onMoveToPendingClicked(story);
                }}
              >
                <Trans>
                  Pending Review
                </Trans>
              </a>
            </div>
            <div className="accordion-option icon-a">
              <a
                aria-label={t("onMoveToArchivedClicked")}
                href=""
                onClick={async e => {
                  e.preventDefault();
                  await onMoveToArchivedClicked(story);
                }}
              >
                <Trans>
                  Archived
                </Trans>
              </a>
            </div>
          </>
        )
      }
    ]);

    setAccordionOptionsArchived([
      {
        content: (
          <>
            <div className="accordion-option icon-a">
              <a
                aria-label={t("onMoveToPendingClicked")}
                href=""
                onClick={async e => {
                  e.preventDefault();
                  await onMoveToPendingClicked(story);
                }}
              >
                <Trans>
                  Pending Review
                </Trans>
              </a>
            </div>
            <div className="accordion-option icon-a">
              <a
                aria-label={t("onMoveToApprovedClicked")}
                href=""
                onClick={async e => {
                  e.preventDefault();
                  await handleApproveClicked(story);
                }}
              >
                <Trans>
                  Approved
                </Trans>
              </a>
            </div>
            <div className="accordion-option icon-a">
              <a
                aria-label={t("onMoveToArchivedClicked")}
                href=""
                onClick={async e => {
                  e.preventDefault();
                  await onMoveToInternalOnlyClicked(story);
                }}
              >
                <Trans>
                  Internal Only
                </Trans>
              </a>
            </div>
          </>
        )
      }
    ]);
  }, [handleApproveClicked, itemWithOriginalTrue,
    onMoveToArchivedClicked, onMoveToInternalOnlyClicked,
    onMoveToPendingClicked, story, subtitles, thirdPartyMedias]);

  const handleDelete = async () => {
    await client.deleteStory({ storyId: story.id });
    fetchedProfile({
      ...profile,
      pendingStoryReviews: profile?.pendingStoryReviews
        ?.filter(pendingStoryId => pendingStoryId !== story.id)
    });
    onDelete(story.id);
  };

  return (
    <QuickOptions className="QuickOptions--Main" withSubdropdown>
      <ul className="dropdown-menu">
        <div className={classnames("accordion-option", "accordion-header")}>
          <AccordianCaptions accordionOptions={accordionOptions} />
        </div>
        {(isMyOwnStory(profile, story) || isAdmin(profile, story)) && (
          <>
            <li>
              <PostStory
                onPauseVideo={onPauseVideo}
                onPosted={onUpdate}
                onAnotherModalOpen={onAnotherModalOpen}
                story={story}
              >
                {onTrigger => (
                  <Button inverted primary onClick={onTrigger}>
                    <Icon icon="edit" />
                    <Trans>Edit Details</Trans>
                  </Button>
                )}
              </PostStory>
            </li>
            {story.visuals.length === 1 && (
              <li>
                <VideoStudioContext
                  isLastMediaVersionProcessing={isLastMediaVersionProcessing}
                  mediaVersionHistory={thirdPartyMedias}
                  onPauseVideo={onPauseVideo}
                  onPosted={onUpdate}
                  onThirdPartyMediasReload={onThirdPartyMediasReload}
                  onUpdateDraftVersion={onUpdateDraftVersion}
                  onAnotherModalOpen={onAnotherModalOpen}
                  story={story}
                  thirdPartyMediaIsFetching={thirdPartyMediaIsFetching}
                />
              </li>
            )}
            <li>
              <ChqModalConfirmDelete
                onAccept={handleDelete}
                trigger={onTrigger => (
                  <PlainButton onClick={onTrigger} className="link action">
                    <Icon icon="trash-a" />
                    <Trans>Delete</Trans>
                  </PlainButton>
                )}
              >
                <Trans>Are you sure you want to delete this story?</Trans>
              </ChqModalConfirmDelete>
            </li>
          </>
        )}
        <li>
          <Flag action="flagStory" options={{ storyId: story.id }}>
            {onTrigger => (
              <PlainButton onClick={onTrigger}>
                <Icon icon="ios-flag" />
                <Trans>Flag story as inappropriate</Trans>
              </PlainButton>
            )}
          </Flag>
        </li>
        <li>
          <StoryShare story={story} inline />
        </li>
        {isAdmin(profile) && (
          <>
            {["pending", "internal"].includes(story.externalState)
            && window.location.pathname !== `/admin/story-reviews/${story.externalState}/story/${story.id}`
            && !isInAdminListPage && (
              <li>
                <NavLink
                  className="sub-menu-item"
                  to={`/admin/story-reviews/${story.externalState}/story/${story.id}`}
                  activeClassName="active"
                  isActive={(match, location) => match || location.pathname === `/admin/story-reviews/${story.externalState}/story/${story.id}`}
                >
                  <Icon icon="android-open" />
                  <Trans>
                    {story.externalState === "pending"
                      ? "View in admin section (Pending Review)"
                      : "View in admin section (Internal)"}
                  </Trans>
                </NavLink>
              </li>
            )}
            {["landing_page", "approved", "social", "approved_for_everything"].includes(story.externalState)
            && window.location.pathname !== `/admin/story-reviews/approved/story/${story.id}`
            && !isInAdminListPage && (
              <li>
                <NavLink
                  className="sub-menu-item"
                  to={`/admin/story-reviews/approved/story/${story.id}`}
                  activeClassName="active"
                  isActive={(match, location) => match || location.pathname === `/admin/story-reviews/approved/story/${story.id}`}
                >
                  <Icon icon="android-open" />
                  <Trans>View in admin section (Approved)</Trans>
                </NavLink>
              </li>
            )}
          </>
        )}
        {isApprovedState && isApprovedPath && (
          <div className={classnames("accordion-option", "accordion-header")}>
            <AccordionStateOptions accordionOptions={accordionOptionsApprove} />
          </div>
        )}

        {isInternalState && isInternalPath && (
          <div className={classnames("accordion-option", "accordion-header")}>
            <AccordionStateOptions accordionOptions={accordionOptionsInternal} />
          </div>
        )}

        {isArchivedState && isArchivedPath && (
          <div className={classnames("accordion-option", "accordion-header")}>
            <AccordionStateOptions accordionOptions={accordionOptionsArchived} />
          </div>
        )}

        {isPendingState && isPendingPath && (
          <li>
            <a
              aria-label={t("onMoveToArchivedClicked")}
              href=""
              onClick={async e => {
                e.preventDefault();
                await onMoveToArchivedClicked(story);
              }}
            >
              <Icon icon="folder" />
              <Trans>
                Move to Archived
              </Trans>
            </a>
          </li>
        )}
      </ul>
    </QuickOptions>
  );
};

export default LightboxStoryQuickOptions;
